import { filter, pick, reduce, toPairs } from "ramda";
import type { DefaultRootState } from "react-redux";
import type { Dispatch } from "redux";
import type { ThunkAction } from "redux-thunk";
import { setToolbarSnack } from "actions/context";
import { SUPPORT_PHONE } from "app_constants";
import { commaJoin } from "helpers/collections";
import { isDevelopment } from "helpers/get_env";
import type { IBrandGroup } from "helpers/transforms/entities";
import {
  buildUserApplicationsAfterCreate,
  isCreateUserFail,
} from "helpers/users/create_user";
import {
  createUser as createUserRequest,
  getAllUsers,
  postHQServiceUser,
  putHQServiceUser,
} from "interfaces/users/all_users";
import { selectFetchedBrandId } from "selectors/users";
import { RequestStatus } from "types/request";
import type { IAppCreateStatus, ICreateUserResponse } from "types/user";
import type { IAppUser, INewUser, IPOSUserInfo } from "types/user/app_user";

export type AllUsersActionTypes =
  | IReceiveAllUsersAction
  | IUpdateUsersRequestStatusAction
  | IUpdateEditableUserAction
  | IUpdateLocalUserRecordAction
  | IClearEditableUserAction
  | ISaveCreateUserResponseAction;

type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  AllUsersActionTypes
>;

export enum TypeKeys {
  CLEAR_EDITABLE_USER = "USERS/ALL_USERS/CLEAR_EDITABLE_USER",
  RECEIVE_ALL_USERS = "USERS/ALL_USERS/RECEIVE_ALL_USERS",
  UPDATE_EDITABLE_USER = "USERS/ALL_USERS/UPDATE_EDITABLE_USER",
  UPDATE_LOCAL_USER_RECORD = "USERS/ALL_USERS/UPDATE_LOCAL_USER_RECORD",
  UPDATE_USERS_REQUEST_STATUS = "USERS/ALL_USERS/UPDATE_USERS_REQUEST_STATUS",
  SAVE_CREATE_USER_RESPONSE = "USERS/ALL_USERS/SAVE_CREATE_USER_RESPONSE",
}

export enum AllUsersRequestKey {
  AllUsers = "AllUsers",
  CreateUser = "CreateUser",
}

export interface IReceiveAllUsersAction {
  type: TypeKeys.RECEIVE_ALL_USERS;
  all: IAppUser[];
  posOnly: IPOSUserInfo[];
  brandId: string;
}

export const receiveUsers = (
  all: IAppUser[],
  posOnly: IPOSUserInfo[],
  brandId: string
): IReceiveAllUsersAction => ({
  type: TypeKeys.RECEIVE_ALL_USERS,
  all,
  posOnly,
  brandId,
});

export interface IUpdateUsersRequestStatusAction {
  type: TypeKeys.UPDATE_USERS_REQUEST_STATUS;
  requestKey: AllUsersRequestKey;
  requestStatus: RequestStatus;
}

export const updateUsersRequestStatus = (
  requestKey: AllUsersRequestKey,
  requestStatus: RequestStatus
): IUpdateUsersRequestStatusAction => ({
  type: TypeKeys.UPDATE_USERS_REQUEST_STATUS,
  requestKey,
  requestStatus,
});

export const fetchAllUsers = (brandGroup: IBrandGroup): ThunkResult<void> => {
  return (dispatch: Dispatch<any>, getState): Promise<void> => {
    const fetchedBrandId = selectFetchedBrandId(getState());
    if (fetchedBrandId === brandGroup.brandId) {
      // TODO - finer tuned caching invalidation
      return Promise.resolve();
    }

    dispatch(
      updateUsersRequestStatus(
        AllUsersRequestKey.AllUsers,
        RequestStatus.REQUESTING
      )
    );

    return getAllUsers(brandGroup)
      .then(({ users, posOnlyUsers }) => {
        dispatch(receiveUsers(users, posOnlyUsers, brandGroup.brandId));
        dispatch(
          updateUsersRequestStatus(
            AllUsersRequestKey.AllUsers,
            RequestStatus.SUCCESS
          )
        );
      })
      .catch((error) => {
        dispatch(
          updateUsersRequestStatus(
            AllUsersRequestKey.AllUsers,
            RequestStatus.FAILURE
          )
        );
        if (isDevelopment()) {
          // eslint-disable-next-line no-console
          console.error("All users fetch:", error);
        }
      });
  };
};

export interface IUpdateEditableUserAction {
  type: TypeKeys.UPDATE_EDITABLE_USER;
  editableUser: IAppUser;
}

export const updateEditableUser = (
  editableUser: IAppUser
): IUpdateEditableUserAction => ({
  type: TypeKeys.UPDATE_EDITABLE_USER,
  editableUser,
});

export interface IClearEditableUserAction {
  type: TypeKeys.CLEAR_EDITABLE_USER;
}

export const clearEditableUser = (): IClearEditableUserAction => ({
  type: TypeKeys.CLEAR_EDITABLE_USER,
});

export interface IUpdateLocalUserRecordAction {
  type: TypeKeys.UPDATE_LOCAL_USER_RECORD;
  user: IAppUser;
}

// Take the user that has been persisted to the hq service
// and merge it with our local users set
export const updateLocalUserRecord = (
  user: IAppUser
): IUpdateLocalUserRecordAction => ({
  type: TypeKeys.UPDATE_LOCAL_USER_RECORD,
  user,
});

export interface ISaveCreateUserResponseAction {
  type: TypeKeys.SAVE_CREATE_USER_RESPONSE;
  response: ICreateUserResponse;
}

export const saveCreateUserResponse = (
  response: ICreateUserResponse
): ISaveCreateUserResponseAction => ({
  type: TypeKeys.SAVE_CREATE_USER_RESPONSE,
  response,
});

const PRODUCT_NAMES: { [index: string]: string } = {
  breadcrumb: "Upserve POS",
  swipely: "Tools & Analytics",
  inventory: "Inventory",
  hq: "HQ",
};

export const createUser = (user: INewUser) => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      updateUsersRequestStatus(
        AllUsersRequestKey.CreateUser,
        RequestStatus.REQUESTING
      )
    );
    // Discard any previously saved response
    dispatch(saveCreateUserResponse({}));
    return createUserRequest(user)
      .then((response: ICreateUserResponse) => {
        const fail = filter(
          ([, status]) => isCreateUserFail(status),
          toPairs<IAppCreateStatus>(response)
        );
        const [failedProducts, failReasons] = reduce(
          ([products, reasons], [appKey, status]) => {
            const productName = PRODUCT_NAMES[appKey];
            if (productName) {
              products.push(productName);
            }
            reasons.push(`${appKey} - ${status.reason || "unknown reason"}`);
            return [products, reasons];
          },
          [[], []] as [string[], string[]],
          fail
        );

        // There can be more failures than requested locations due to hq_service creation
        if (user.locations && fail.length >= user.locations.length) {
          // Everything Failed
          dispatch(
            setToolbarSnack({
              id: "create_user_status",
              message: `Unable to add user. Please try again. If you continue to have issues, contact support at ${SUPPORT_PHONE}.`,
              type: "error",
            })
          );
          // Reset to NOT_STARTED to give a chance to retry
          dispatch(
            updateUsersRequestStatus(
              AllUsersRequestKey.CreateUser,
              RequestStatus.NOT_STARTED
            )
          );
        } else {
          // COMPLETE or PARTIAL SUCCESS
          // Save user record to local state because FE must represent
          // partial creation and prevent retry using same email.
          const createdUser: IAppUser = pick(
            ["id", "lastName", "firstName", "email", "applications"],
            user
          );
          createdUser.applications = buildUserApplicationsAfterCreate(
            user,
            response
          );
          dispatch(updateLocalUserRecord(createdUser));
          dispatch(
            updateUsersRequestStatus(
              AllUsersRequestKey.CreateUser,
              RequestStatus.SUCCESS
            )
          ); // redirects to browse list in create_user_container

          if (fail.length) {
            dispatch(
              setToolbarSnack({
                id: "create_user_status",
                message: `User created, but with limited permissions due to an issue. Contact support at ${SUPPORT_PHONE} to ensure they’re set up correctly.`,
                type: "error",
              })
            );
            // eslint-disable-next-line no-console
            console.error(
              `Failed to create user on ${commaJoin(failedProducts)}:`,
              failReasons.join(", ")
            );
          } else {
            dispatch(
              setToolbarSnack({
                id: "create_user_status",
                message: `${user.firstName} ${user.lastName} has been sent an invite.`,
                type: "success",
              })
            );
          }
        }
      })
      .catch((error) => {
        dispatch(
          updateUsersRequestStatus(
            AllUsersRequestKey.CreateUser,
            RequestStatus.FAILURE
          )
        );
        dispatch(
          setToolbarSnack({
            id: "create_user_status",
            message: `There was an error creating this user.`,
            type: "error",
          })
        );
        // eslint-disable-next-line no-console
        console.error("Failed to create user:", error);
        window.location.href = "https://upserve.com";
      });
  };
};

export const createOrUpdateHQServiceUser = (
  user: IAppUser,
  brand: string
): ThunkResult<Promise<void>> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    const isUpdate = !!user.id;
    const apiInterface = isUpdate ? putHQServiceUser : postHQServiceUser;
    return apiInterface(user, brand)
      .then((savedUser: IAppUser) => {
        dispatch(
          setToolbarSnack({
            id: "create_hq_user_status",
            message: `User ${isUpdate ? "updated" : "created"} successfully.`,
            type: "success",
          })
        );
        const userWithHQApp: IAppUser = {
          ...savedUser,
          applications: {
            ...user.applications,
            hq: [
              {
                id: savedUser.id,
                roles: [],
              },
            ],
          },
        };
        dispatch(updateLocalUserRecord(userWithHQApp));
        dispatch(updateEditableUser(userWithHQApp));
      })
      .catch(() => {
        dispatch(
          setToolbarSnack({
            id: "create_hq_user_status",
            message: `There was an error ${
              isUpdate ? "updating" : "creating"
            } this user.`,
            type: "error",
          })
        );
      });
  };
};
